import { useState, useCallback } from 'react';
import { CloseIcon, ButtonGray, FileItem, tools } from '@halo/ui-chat-react';
import type { FileUploadFileMimeType } from '@chakra-ui/react';
import { Center, HStack, For, Button, VStack, createOverlay } from '@chakra-ui/react';
import { Dialog, Portal } from '@halo/ui-react';
import { usePickFiles } from '@halo/hooks';
import { fileTypeFromStream, supportedMimeTypes } from 'file-type';

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;
const MIN_FILE_SIZE = 0;
const MAX_FILE_COUNT = 100;

export async function filter(files: File[], source: PickFileDialogSource) {
  let _ = await Promise.all(
    files.map((file) => {
      if (file.size >= MAX_FILE_SIZE) return null;
      if (file.size <= MIN_FILE_SIZE) return null;
      return file;
    })
  );
  if (source === PickFileDialogSource.IMAGE) {
    // filter fake image/video
    _ = await Promise.all(
      _.map(async (file) => {
        if (!file) return null;
        // if fake image ext
        const ext = file.name.split('.').pop() || '';
        if (supportedMimeTypes.has(`image/${ext}`)) {
          const fileType = await fileTypeFromStream(file.stream());
          if (!fileType?.mime.startsWith('image/')) return null;
        }
        if (supportedMimeTypes.has(`video/${ext}`)) {
          const fileType = await fileTypeFromStream(file.stream());
          if (!fileType?.mime.startsWith('video/')) return null;
        }
        return file;
      })
    );
  }

  return _.filter((_) => !!_);
}

export function getAccept(
  source: PickFileDialogSource
): FileUploadFileMimeType[] | FileUploadFileMimeType {
  if (source === PickFileDialogSource.IMAGE) return ['image/*', 'video/*'];
  return '*/*';
}

export enum PickFileDialogSource {
  IMAGE,
  FILE,
}

export const PickFileDialogName = 'pickFileDialog';

interface PickFileDialogProps {
  files?: File[];
  sessionName: string;
  /* onAdd: () => void; */
  source: PickFileDialogSource;
}

export const pickFileDialog = createOverlay<PickFileDialogProps>((props) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!props.open) return;
    setFiles((props.files || []).slice(0, 100));
  }, [props.open, props.files]);

  const handleSend = useCallback(async () => {
    await pickFileDialog.close(PickFileDialogName, { files });
    pickFileDialog.remove(PickFileDialogName);
  }, [files]);

  const handleClose = useCallback(async () => {
    await pickFileDialog.close(PickFileDialogName, { files: [] });
    pickFileDialog.remove(PickFileDialogName);
  }, []);

  const handleDelete = useCallback(
    async (file: File) => {
      if (files.length === 1) {
        handleClose().then(() => {
          setFiles((files) => files.filter((f) => f !== file));
        });
        return;
      }
      setFiles((files) => files.filter((f) => f !== file));
    },
    [files, handleClose]
  );

  useKeyPress(
    'Enter',
    (e) => {
      if (!props.open) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handleSend();
    },
    { useCapture: true }
  );

  const { pick } = usePickFiles();

  return (
    <Dialog.Root
      placement="center"
      scrollBehavior="inside"
      lazyMount
      unmountOnExit
      trapFocus={false}
      {...props}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="2xl" w="428px" maxW="100%" px="5" pt="4" pb="5">
            <Dialog.Header
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
              py="2.5"
              px="0"
            >
              <Dialog.Title truncate>
                {t('chat.sendToText')} {props.sessionName}
              </Dialog.Title>
              <Center color="fg.subtle" cursor="pointer" onClick={handleClose}>
                <CloseIcon />
              </Center>
            </Dialog.Header>
            <Dialog.Body className="scrollHover" px="0" pb="0" maxH="300px">
              <VStack gap="2">
                <For each={files}>
                  {(section, index) => (
                    <FileItem key={index} file={section} handleDelete={handleDelete} />
                  )}
                </For>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer display="flex" justifyContent="space-between" p="0" mt="2">
              <Button
                variant="ghost"
                rounded="xl"
                color="brand"
                onClick={() => {
                  pick({
                    accept: getAccept(props.source),
                    multiple: true,
                    onFiles: (files) => {
                      filter(files, props.source).then((_) => {
                        if (_.length === 0) {
                          tools.toaster.error({
                            title: t('tips.noValidFilesText'),
                          });
                          return;
                        }
                        if (_.length !== files.length) {
                          tools.toaster.error({
                            title: t('tips.invalidFilesFilteredOutText', {
                              count: files.length - _.length,
                            }),
                          });
                        }
                        setFiles((prev) => [...prev, ..._].slice(0, MAX_FILE_COUNT));
                      });
                    },
                  });
                }}
              >
                {t('buttons.add')}
              </Button>
              <HStack gap="4">
                <ButtonGray bg="bg.muted" rounded="xl" onClick={handleClose}>
                  {t('buttons.cancel')}
                </ButtonGray>

                <Button rounded="xl" onClick={handleSend}>
                  {t('buttons.send')} ({files.length})
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
